/**
 * UserManagementTab.tsx — the Users tab of the admin panel (T-411, Slice 1 of the T-409 spec).
 *
 * Three deliberate choices, each of which is a failure mode avoided rather than a preference:
 *
 * 1. NO CACHING. Every load is a live read; every action re-reads afterwards. The rows carry
 *    controls that change someone's access, so a stale row plus an action IS the failure mode.
 *    (This is also why there is no react-query here — it is used in exactly one place in the app,
 *    and adding a caching layer to a screen whose correctness argument is "do not cache" would
 *    work against the design.)
 *
 * 2. A FAILED LOAD RENDERS NO ACTIONS. Not disabled ones — none. An admin must never be able to
 *    act on a list that did not load.
 *
 * 3. ZERO USERS IS A FAULT. You are signed in, so you are in the list. A confident "No users yet"
 *    painted over a broken query is exactly the lie this screen must not tell.
 *
 * Slice 1 has no disable/enable/delete: those need IAM permissions beyond this slice's scope.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  fetchUsers,
  inviteUser,
  resendInvite,
  setAdminRole,
  type AdminUserRow,
  type AdminUsersMeta,
} from '@/lib/adminUsersApi'
import { InviteUserForm } from './InviteUserForm'
import { UserRow } from './UserRow'

const MAILER_LABEL: Record<string, string> = {
  cognito_default: "Cognito's default mailer (unbranded sender, shared daily budget)",
  ses: 'Amazon SES (branded sender)',
}

export function UserManagementTab() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null)
  const [meta, setMeta] = useState<AdminUsersMeta | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchUsers()
      setUsers(res.users)
      setMeta(res.meta)
    } catch (e) {
      setUsers(null) // drop any previous rows — never leave actionable rows on screen after a failure
      setError((e as Error).message)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /** Run an action, then ALWAYS re-read from the server rather than patching local state. */
  const act = useCallback(
    async (fn: () => Promise<string | null>) => {
      setBusy(true)
      setError(null)
      setNotice(null)
      try {
        const message = await fn()
        if (message) setNotice(message)
        await load()
      } catch (e) {
        setError((e as Error).message)
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const onInvite = (input: { email: string; makeAdmin: boolean; note?: string }) =>
    void act(async () => {
      const res = await inviteUser(input)
      // 207: the account EXISTS but the role did not stick. Reporting this as a plain success
      // would tell the admin the role was set when it was not.
      //
      // The condition reads the SERVER's answer (`roleAssigned`), not what we asked for — the
      // response is authoritative, and a client that decides from its own request would keep
      // reporting success if the server's contract ever changed underneath it.
      if (!res.roleAssigned) {
        setError(res.error ?? 'Invited, but the admin role could not be assigned.')
        return null
      }
      return `Invite sent to ${input.email}.`
    })

  const onResend = (username: string) =>
    void act(async () => {
      await resendInvite(username)
      return `New invite sent to ${username}. Their previous temporary password no longer works.`
    })

  const onToggleAdmin = (row: AdminUserRow) =>
    void act(async () => {
      const res = await setAdminRole(row.username, row.isAdmin ? 'remove' : 'add')
      return res.notice
    })

  const remaining = meta ? Math.max(0, meta.dailyInviteCap - meta.invitesSentToday) : null

  return (
    <div className="space-y-4">
      {meta ? (
        <p
          role="status"
          aria-label="Invite delivery"
          className="rounded-md border border-border p-2 text-xs text-muted-foreground"
        >
          Invites are delivered by {MAILER_LABEL[meta.mailer] ?? meta.mailer}.{' '}
          <strong>
            {remaining} of {meta.dailyInviteCap} invites left today
          </strong>{' '}
          — the budget is shared with password-reset emails for existing users.
        </p>
      ) : null}

      <InviteUserForm
        validityDays={meta?.inviteValidityDays ?? 7}
        busy={busy}
        onInvite={onInvite}
      />

      {error ? (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Retry
          </button>
        </div>
      ) : null}

      {notice ? (
        <p role="status" aria-label="Result" className="text-sm text-foreground">
          {notice}
        </p>
      ) : null}

      {!users && !error ? <p className="text-sm text-muted-foreground">Loading users…</p> : null}

      {users && users.length === 0 ? (
        <p className="text-sm text-destructive">
          No users returned. This is unexpected — you are signed in, so at least your own account
          should appear. Treat this as a failed read rather than an empty list.
        </p>
      ) : null}

      {users && users.length > 0 ? (
        <ul aria-label="Users" className="text-sm">
          {users.map((u) => (
            <UserRow
              key={u.username}
              row={u}
              busy={busy}
              onResend={onResend}
              onToggleAdmin={onToggleAdmin}
            />
          ))}
        </ul>
      ) : null}
    </div>
  )
}
